/**
 * LAS DOS PRIMERAS SECCIONES DEL MENSUAL (rediseño del 28-10).
 *
 * «TU PESO». Si hoy ya se pesó (en el check-in), la sección se salta y el peso del reporte es
 * el de referencia de la semana (regla del 29-10; lo decide `FormularioReporte` con
 * `resumen.peso_de_la_semana`, de `core/mensual`). Si no, el mismo campo del check-in diario («Tu peso del día», con su
 * «Último registro») y el botón naranja «REGISTRAR PESO». El peso se guarda en su serie como
 * cualquier pesaje (`CampoDePeso`, el único que la escribe) y es el del reporte.
 *
 * «TU MES» (maqueta 1). La pantalla del informe del quincenal con las tres últimas semanas,
 * la de hoy incluida: TU PESO y LO QUE HAS HECHO (sin «Cómo te has sentido», 28-10). Todo viene
 * contado de `GET /reports/mensual/resumen`; aquí no se calcula nada. Las filas sin dato no
 * salen, y si no queda ninguna: «No tienes datos registrados en estos X días».
 */
import React, { useRef, useState } from 'react';
import CampoDePeso from '../CampoDePeso';
import { BarraDeTramos, FilaDelResumen as Fila, ValorDelResumen as Valor } from './piezas';

const ORANGE = '#FF671F';

const Rotulo = ({ children }) => (
    <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: ORANGE }}>{children}</p>
);

/** Una tarjeta de «Tu mes» con su rótulo encima, en naranja como en la maqueta. */
const Tarjeta = ({ titulo, pie, children, testid, destacada = false }) => (
    <div className="space-y-2" data-testid={testid}>
        <p className="flex items-baseline gap-1.5">
            <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: ORANGE }}>{titulo}</span>
            {pie && <span className="text-[11px] text-muted-foreground">· {pie}</span>}
        </p>
        <div className="rounded-2xl bg-card border px-4 py-1.5" style={{ borderColor: destacada ? ORANGE : undefined }}>
            {children}
        </div>
    </div>
);

const SinDatos = ({ texto, testid }) => (
    <p className="py-3 text-sm text-muted-foreground" data-testid={testid}>{texto}</p>
);

/**
 * «TU PESO»: el campo del check-in y «REGISTRAR PESO». Si al guardarlo hay que elegir el peso de
 * referencia semanal (ya había otros esa semana, regla del 29-10), primero eso.
 */
// `sinRotulo` y `abrir`: el check-in diario (28-10), que lleva «Tu peso del día» en su cabecera
// y solo pone el foco en la casilla si viene a pesarse.
export const MensualTuPeso = ({ api, ultimo, onRegistrado, sinRotulo = false, abrir = true }) => {
    const [pesado, setPesado] = useState(null);
    const [semana, setSemana] = useState(null);
    // Los avisos de `CampoDePeso` llegan con la pantalla de cuando se pulsó: el peso, aquí.
    const pesadoRef = useRef(null);
    const listo = pesado && !semana?.ofrecer;
    return (
        <div className="space-y-2" data-testid="mensual-tu-peso">
            {!sinRotulo && <Rotulo>Tu peso del día</Rotulo>}
            <div className="rounded-2xl border border-border bg-card p-4">
                <CampoDePeso api={api} ultimo={pesado || ultimo} diasAtras={0} abrir={abrir} soloHoy botonAbajo
                    conLosDosNumeros={false}
                    // Guardado: es ya el peso del reporte. Pasa de sección cuando se sabe si
                    // toca elegir el de referencia semanal; si eso falla, con «Continuar».
                    onGuardado={(p) => { pesadoRef.current = p; setPesado(p); onRegistrado(p, false); }}
                    onSemana={(s) => {
                        setSemana(s);
                        if (pesadoRef.current && !s?.ofrecer) onRegistrado(pesadoRef.current, true);
                    }} />
            </div>
            {listo && (
                <button type="button" onClick={() => onRegistrado(pesado, true)} data-testid="mensual-peso-continuar"
                    className="w-full rounded-xl py-3 text-sm font-bold uppercase tracking-wider text-black"
                    style={{ backgroundColor: ORANGE }}>
                    Continuar
                </button>
            )}
        </div>
    );
};

/** «TU MES», con el resumen ya cargado (o cargándose, o fallido). */
export const MensualTuMes = ({ resumen, cargando, fallo, onReintentar }) => {
    if (cargando && !resumen) {
        return (
            <div className="animate-pulse space-y-3" data-testid="mensual-tu-mes">
                <div className="h-20 bg-card rounded-2xl" />
                <div className="h-44 bg-card rounded-2xl" />
            </div>
        );
    }
    if (fallo && !resumen) {
        return (
            <div className="rounded-2xl border border-border bg-card p-4" data-testid="mensual-tu-mes">
                <p className="text-sm text-foreground">No hemos podido traer tus datos de estos días.</p>
                <button type="button" onClick={onReintentar} data-testid="mensual-resumen-reintentar"
                    className="mt-2 text-sm font-bold" style={{ color: ORANGE }}>
                    Probar otra vez
                </button>
            </div>
        );
    }
    const peso = resumen?.peso || {};
    const cabecera = peso.cabecera;
    // SIN DATO, SIN FILA (28-10): ni «Sin datos» ni «Sin rutina cargada», «No llevas cardio
    // pautado»... La fila que no tiene número no sale.
    const filasDelPeso = (peso.filas || []).filter((f) => !f.sin_dato);
    const filasDeLoHecho = (resumen?.hecho || []).filter((f) => !f.sin_dato);
    return (
        <div className="space-y-4" data-testid="mensual-tu-mes">
            {cabecera && (
                <div className="text-center py-2" data-testid="mensual-cifra">
                    <p className={`text-4xl font-bold tabular-nums ${cabecera.numero < 0 ? 'text-emerald-500' : 'text-foreground'}`}
                        style={{ fontFamily: 'Barlow Condensed' }}>
                        {cabecera.valor}
                    </p>
                    <p className="text-xs text-muted-foreground">{cabecera.pie}</p>
                </div>
            )}

            {filasDelPeso.length > 0 && (
            <Tarjeta titulo="Tu peso" testid="mensual-peso" destacada>
                {filasDelPeso.map((f, i) => (
                    <Fila key={f.clave} primera={!i} testid={`mensual-peso-${f.clave}`}
                        etiqueta={(
                            <span className="block">
                                <span className="block">{f.etiqueta}</span>
                                {f.pie && <span className="block text-[11px] text-muted-foreground">{f.pie}</span>}
                            </span>
                        )}>
                        <span className="flex items-baseline gap-1.5">
                            <Valor fila={f} />
                            {f.dif && <span className="text-[11px] text-muted-foreground tabular-nums">{f.dif}</span>}
                        </span>
                    </Fila>
                ))}
            </Tarjeta>
            )}

            <Tarjeta titulo="Lo que has hecho" testid="mensual-hecho">
                {resumen?.hecho_sin_datos || !filasDeLoHecho.length ? (
                    <SinDatos texto={resumen?.hecho_sin_datos
                        || `No tienes datos registrados en estos ${resumen?.periodo?.dias || 0} días`}
                        testid="mensual-hecho-sin-datos" />
                ) : filasDeLoHecho.map((f, i) => (
                    <div key={f.clave} data-testid={`mensual-fila-${f.clave}`}
                        className={`py-2.5 space-y-1.5 ${i ? 'border-t border-border' : ''}`}>
                        <div className="flex items-center justify-between gap-3">
                            <span className="text-sm text-foreground/80">{f.etiqueta}</span>
                            <Valor fila={f} />
                        </div>
                        <BarraDeTramos hechos={f.hecho} de={f.de} />
                    </div>
                ))}
            </Tarjeta>

        </div>
    );
};
